import React from 'react';
import PropTypes from 'prop-types';
import utils from "../utils/utils";
import {Page, Text, View, Document, StyleSheet, Font} from '@react-pdf/renderer';
import DATE_FORMAT from "../components/constants/dateFormat";
import regularFont from "../assets/fonts/Metropolis-Regular.ttf"
import boldFont from "../assets/fonts/Metropolis-SemiBold.ttf"

const MarketplaceAdminBillPdfDocument = ({bill, copy}) => {
  if (!bill.from && !bill.fromName || !bill.to && !bill.toName) {
    return (<Document></Document>);
  }

  const BORDER_COLOR = '#bfbfbf'
  const BORDER_STYLE = 'solid'

  Font.register({
    family: 'metropolis',
    fonts: [
      { src: regularFont },
      { src: boldFont, fontWeight: 600 }
    ]
  });

  const showPriceWithoutVatInBillItem = bill.items
    .some(bi => bi.priceWithoutVat) && (bill.fromVatNumber || bill.from && bill.from.vatNumber);

  const styles = StyleSheet.create({
    body: {
      padding: 30,
      fontSize: 12,
      fontFamily: 'metropolis',
    },
    title: {
      fontSize: 24,
      marginTop: 50,
      marginBottom: 50,
    },
    h3: {
      fontSize: 16,
    },
    bold: {
      fontWeight: 700,
    },
    companies: {
      flexDirection: "row",
      marginBottom: 30,
    },
    dates: {
      flexDirection: "row",
      marginBottom: 50,
    },
    company: {
      width: '50%',
    },
    table: {
      display: "table",
      width: "auto",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0
    },
    tableRow: {
      margin: "auto",
      flexDirection: "row"
    },
    tableCol1Header: {
      width: '50%',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableColHeaderHalf: {
      width: '25%',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableColHeaderThird: {
      width: '16.6%',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCol1: {
      width: '50%',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableColHalf: {
      width: "25%",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableColThird: {
      width: "16.6%",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCellHeader: {
      margin: 5,
      fontWeight: 700
    },
    tableCell: {
      margin: 5,
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
    }
  });

  const from = bill.from? getBillFromPropertiesFromBillDetails(bill, styles, copy): (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.from.companyName}: </Text><Text>
          {bill.fromName}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.registryCode}: </Text><Text>
          {bill.fromRegistryCode}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.address}: </Text><Text>
          {bill.fromAddress}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.vatNumber}: </Text><Text>
          {bill.fromVatNumber ||  '-'}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.iban}: </Text><Text>
          {bill.fromIban}
        </Text>
      </Text>
    </View>
  );

  const to = bill.to? getBillToPropertiesFromBillDetails(bill, styles, copy): (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.to.companyName}: </Text><Text>
          {bill.toName}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.registryCode}: </Text><Text>
          {bill.toRegistryCode}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.address}: </Text><Text>
          {bill.toAddress}
        </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.vatNumber}: </Text><Text>
          {bill.toVatNumber ||  '-'}
        </Text>
      </Text>
    </View>
  );

  const deadline = utils.getDateFromString(bill.deadline);
  const formattedDeadline = deadline ? deadline.format(DATE_FORMAT.display) : '-';

  const showPricesWithVat = bill.fromVatNumber || bill.from && bill.from.vatNumber;

  const contactEmail = bill.fromEmail || bill.from && bill.from.contactDetails.email;
  const contactPhone = bill.fromPhoneNumber || bill.from && bill.from.contactDetails.phoneNumber;

  return (
    <Document>
      <Page style={styles.body}>
        <Text style={styles.title}>
          {copy.body.header}
        </Text>

        <View style={styles.companies}>
          {to}

          {from}
        </View>

        <View style={styles.dates}>
          <View style={[styles.company, {paddingRight: 15}]}>
            <Text>
              <Text style={styles.bold}>{copy.body.dateCreated}: </Text><Text>
              {utils.getDateFromString(bill.dateCreated).format(DATE_FORMAT.display)}
            </Text>
            </Text>
            <Text>
              <Text style={styles.bold}>{copy.body.deadline}: </Text><Text>
              {formattedDeadline? formattedDeadline: '-'}
            </Text>
            </Text>
          </View>
        </View>

        <View style={[styles.table, {marginBottom: 5}]}>
          <View style={styles.tableRow}>
            <View style={styles.tableCol1Header}>
              <Text style={styles.tableCellHeader}>
                {copy.table.product}
              </Text>
            </View>
            <View style={showPriceWithoutVatInBillItem? styles.tableColHeaderThird: styles.tableColHeaderHalf}>
              <Text style={styles.tableCellHeader}>
                {copy.table.quantity}
              </Text>
            </View>
            {showPriceWithoutVatInBillItem? (
              <View style={showPriceWithoutVatInBillItem? styles.tableColHeaderThird: styles.tableColHeaderHalf}>
                <Text style={styles.tableCellHeader}>
                  {copy.table.totalWithoutVat}
                </Text>
              </View>
            ): <View></View>}

            <View style={showPriceWithoutVatInBillItem? styles.tableColHeaderThird: styles.tableColHeaderHalf}>
              <Text style={styles.tableCellHeader}>
                {copy.table.total}
              </Text>
            </View>
          </View>
          {
            bill.items.map((billItem, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={styles.tableCol1}>
                  <Text style={styles.tableCell}>{billItem.name}</Text>
                </View>
                <View style={showPriceWithoutVatInBillItem? styles.tableColThird: styles.tableColHalf}>
                  <Text style={styles.tableCell}>{billItem.quantity}</Text>
                </View>
                {showPriceWithoutVatInBillItem? (
                  <View style={showPriceWithoutVatInBillItem? styles.tableColThird: styles.tableColHalf}>
                    <Text style={styles.tableCell}>{billItem.priceWithoutVat}€</Text>
                  </View>
                ): <View></View>}
                <View style={showPriceWithoutVatInBillItem? styles.tableColThird: styles.tableColHalf}>
                  <Text style={styles.tableCell}>{billItem.price}€</Text>
                </View>
              </View>
            ))
          }
        </View>
        {
          showPricesWithVat? (
            <Text style={[{textAlign: 'right', marginBottom: 5, marginTop: 5}]}>
              <Text>{copy.total.withoutVat}: </Text><Text style={[styles.bold]}>
              {bill.amountWithoutVat}€</Text>
            </Text>
          ): (<Text></Text>)
        }
        {
          showPricesWithVat && bill.vat? (
            <Text style={[{textAlign: 'right', marginBottom: 10, marginTop: 5}]}>
              <Text>{copy.total.vat}: </Text><Text style={[styles.bold]}>
              {bill.vat}€</Text>
            </Text>
          ): (<Text></Text>)
        }
        <Text style={[styles.h3, {textAlign: 'right'}]}>
          <Text>{copy.total.total}: </Text><Text style={[styles.bold]}>
          {bill.amount}€</Text>
        </Text>

        <View style={styles.footer}>
          <View style={styles.company}>
            <Text>
              <Text style={styles.bold}>{copy.contact.email}: </Text><Text>
              {contactEmail}
            </Text>
            </Text>
            <Text>
              <Text style={styles.bold}>{copy.contact.phoneNumber}: </Text><Text>
              {contactPhone}
            </Text>
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

function getBillFromPropertiesFromBillDetails(bill, styles, copy) {
  return bill.from.className === 'CompanyBillDetails'? (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.from.companyName}: </Text><Text>
        {bill.from.companyName}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.registryCode}: </Text><Text>
        {bill.from.registryCode}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.address}: </Text><Text>
        {bill.from.address.display}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.vatNumber}: </Text><Text>
        {bill.from.vatNumber ||  '-'}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.iban}: </Text><Text>
        {bill.from.iban}
      </Text>
      </Text>
    </View>
  ): (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.from.fullName}: </Text><Text>
        {bill.from.fullName}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.address}: </Text><Text>
        {bill.from.address.display}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.from.iban}: </Text><Text>
        {bill.from.iban}
      </Text>
      </Text>
    </View>
  );
}

function getBillToPropertiesFromBillDetails(bill, styles, copy) {
  return bill.to.className === 'CompanyBillDetails'? (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.to.companyName}: </Text><Text>
        {bill.to.companyName}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.registryCode}: </Text><Text>
        {bill.to.registryCode}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.address}: </Text><Text>
        {bill.to.address.display}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.vatNumber}: </Text><Text>
        {bill.to.vatNumber ||  '-'}
      </Text>
      </Text>
    </View>
  ): (
    <View style={[styles.company, {paddingRight: 15}]}>
      <Text>
        <Text style={styles.bold}>{copy.to.fullName}: </Text><Text>
        {bill.to.fullName}
      </Text>
      </Text>
      <Text>
        <Text style={styles.bold}>{copy.to.address}: </Text><Text>
        {bill.to.address.display}
      </Text>
      </Text>
    </View>
  );
}

MarketplaceAdminBillPdfDocument.propTypes = {
  bill: PropTypes.object.isRequired,
  copy: PropTypes.object.isRequired,
};

MarketplaceAdminBillPdfDocument.defaultProps = {};

export default MarketplaceAdminBillPdfDocument;